/**
 * The dashboard's only way to the API (E01-T04): typed from api/openapi.yaml (schema.d.ts, generated
 * by `pnpm api:generate`; CI fails when it drifts), through the BFF at /bff/api (design 11 §5).
 *
 * The browser holds no token: the BFF's httpOnly session cookie goes along, and every write carries
 * the session's CSRF token (from /bff/me) in X-CSRF-Token. A failed request throws an ApiError with
 * the API's problem (RFC 9457), request id and docs link included.
 */
import createClient, { type Client, type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];

/** The API's error body (application/problem+json). */
export interface Problem {
  type: string;
  title: string;
  status: number;
  code: string;
  detail?: string;
  request_id?: string;
  docs_url?: string;
  errors?: { field: string; message: string }[];
}

/** The role acted in needs a second factor the session was signed in without (E12-T01). */
export function needsStepUp(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.problem.code === "mfa_required";
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(status: number, problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

async function problemOf(response: Response): Promise<Problem> {
  try {
    const body: unknown = await response.clone().json();
    if (body && typeof body === "object" && "code" in body && "title" in body) return body as Problem;
  } catch {
    // not JSON: a proxy's error page, a network failure
  }
  return { type: "about:blank", title: response.statusText || "Request failed", status: response.status, code: "unknown" };
}

const SAFE = new Set(["GET", "HEAD", "OPTIONS"]);

export interface ApiOptions {
  /** Where the BFF is: the same origin by default. */
  origin?: string;
  /** The session's CSRF token, read when a write is sent. */
  csrfToken?: () => string | null;
  fetch?: typeof fetch;
}

export type Api = Client<paths>;

export function createApi(options: ApiOptions = {}): Api {
  const client = createClient<paths>({
    baseUrl: `${options.origin ?? ""}/bff/api`,
    credentials: "same-origin",
    fetch: options.fetch ?? ((request) => fetch(request)),
  });
  const session: Middleware = {
    onRequest({ request }) {
      const token = options.csrfToken?.();
      if (!SAFE.has(request.method) && token) request.headers.set("X-CSRF-Token", token);
      return request;
    },
    async onResponse({ response }) {
      if (!response.ok) throw new ApiError(response.status, await problemOf(response));
      return response;
    },
  };
  client.use(session);
  return client;
}

/** The data of a request that succeeded (a failure has already thrown an ApiError). */
export async function data<T>(request: Promise<{ data?: T; response: Response }>): Promise<T> {
  const { data: body } = await request;
  return body as T;
}

// ------------------------------------------------------------------ the BFF's own endpoints
export type Role = Schemas["MemberOut"]["role"];

/** GET /bff/me: who is signed in, their tenants, the one they act in, its role, the CSRF token. */
export interface Me {
  user: { id: string; email: string; name: string };
  tenants: { id: string; name: string; slug: string; role: Role }[];
  tenant_id: string | null;
  role: Role | null;
  /** What the role allows in that tenant (`agents:write`, …): offer only what will work. */
  permissions: string[];
  csrf_token: string;
}

export interface Bff {
  api: Api;
  /** The session, or null when nobody is signed in. */
  me(): Promise<Me | null>;
  switchTenant(tenantId: string): Promise<void>;
  /** Ends the session; returns the identity provider's logout page to visit, if it has one. */
  logout(): Promise<string | null>;
  /** Sign in (again), back to `returnTo`; `mfa`: with a second factor (the role needs one, E12). */
  loginUrl(returnTo: string, options?: { mfa?: boolean }): string;
  /** A WebSocket to the API as the signed-in user (a test session's stream: `path` like /v1/…). */
  openStream(path: string): WebSocket;
  /** GET /bff/brand: the brand this address shows before sign-in (V1-E14), or null: Swaralo's own. */
  brand(): Promise<unknown>;
}

export function createBff(options: Omit<ApiOptions, "csrfToken"> & { WebSocket?: typeof WebSocket } = {}): Bff {
  const origin = options.origin ?? "";
  const send = options.fetch ?? ((request: Request) => fetch(request));
  let csrf: string | null = null;

  async function bff(method: string, path: string, body?: unknown): Promise<Response> {
    const headers = new Headers({ Accept: "application/json" });
    if (body !== undefined) headers.set("Content-Type", "application/json");
    if (!SAFE.has(method) && csrf) headers.set("X-CSRF-Token", csrf);
    const request = new Request(`${origin}/bff${path}`, {
      method,
      headers,
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const response = await send(request);
    if (!response.ok && response.status !== 401) throw new ApiError(response.status, await problemOf(response));
    return response;
  }

  return {
    api: createApi({ ...options, csrfToken: () => csrf }),
    async me() {
      const response = await bff("GET", "/me");
      if (response.status === 401) {
        csrf = null;
        return null;
      }
      const me = (await response.json()) as Me;
      csrf = me.csrf_token;
      return me;
    },
    async switchTenant(tenantId) {
      const response = await bff("POST", "/tenant", { tenant_id: tenantId });
      if (response.status === 401) throw new ApiError(401, await problemOf(response));
    },
    async logout() {
      const response = await bff("POST", "/logout");
      csrf = null;
      if (response.status === 401) return null;
      return ((await response.json()) as { logout_url: string | null }).logout_url;
    },
    loginUrl(returnTo, { mfa = false } = {}) {
      return `${origin}/bff/login?${new URLSearchParams({ ...(mfa ? { mfa: "1" } : {}), return_to: returnTo })}`;
    },
    async brand() {
      try {
        return await (await bff("GET", "/brand")).json();
      } catch {
        return null; // no brand to show is never a reason not to show the dashboard
      }
    },
    openStream(path) {
      // the session cookie goes along; the BFF checks the page's origin, as it cannot check a CSRF header
      const base = new URL(origin || window.location.origin);
      base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
      const socket = new (options.WebSocket ?? WebSocket)(`${base.origin}/bff/api${path}`);
      socket.binaryType = "arraybuffer";
      return socket;
    },
  };
}
